import { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { AdminAPI, extractError } from "@/lib/api";
import { useFormatters } from "@/lib/format";

const ACTION_TYPES = [
    { value: "any",                 label: "All actions" },
    { value: "admin_login",         label: "Admin login" },
    { value: "admin_login_failed",  label: "Admin login failed" },
    { value: "impersonate_start",   label: "Impersonate · start" },
    { value: "impersonate_end",     label: "Impersonate · end" },
    { value: "tenant_create",       label: "Tenant · create" },
    { value: "tenant_update",       label: "Tenant · update" },
    { value: "tenant_suspend",      label: "Tenant · suspend" },
    { value: "tenant_unsuspend",    label: "Tenant · unsuspend" },
    { value: "user_force_logout",   label: "User · force logout" },
];

function ActionBadge({ action }) {
    const color = action.startsWith("impersonate")
        ? "border-red-300 bg-red-100 text-red-700"
        : action.startsWith("tenant_suspend") || action.endsWith("failed")
            ? "border-amber-300 bg-amber-100 text-amber-800"
            : "border-zinc-300 bg-zinc-100 text-zinc-700";
    return (
        <Badge className={`${color} hover:${color}`} data-testid={`action-${action}`}>
            {action}
        </Badge>
    );
}

export default function AdminAuditLogPage() {
    const { formatDateTime } = useFormatters();
    const [filters, setFilters] = useState({ action_type: "any", actor_user_id: "", date_from: "", date_to: "" });
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        const params = { page, page_size: 50 };
        if (filters.action_type !== "any") params.action_type = filters.action_type;
        if (filters.actor_user_id.trim()) params.actor_user_id = filters.actor_user_id.trim();
        if (filters.date_from) params.date_from = new Date(filters.date_from).toISOString();
        if (filters.date_to) params.date_to = new Date(filters.date_to).toISOString();
        try {
            const r = await AdminAPI.listAuditLog(params);
            setItems(r.items || []);
            setTotal(r.total || 0);
        } catch (err) {
            toast.error(extractError(err));
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    useEffect(() => { load(); }, [load]);

    const pageCount = Math.max(1, Math.ceil(total / 50));

    return (
        <div className="space-y-6" data-testid="admin-audit-page">
            <header>
                <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">
                    Platform · Audit log
                </div>
                <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                    Platform audit log
                </h1>
                <p className="mt-1 text-sm text-zinc-500">
                    Every cross-tenant operation: impersonation, tenant create/update/suspend, and
                    admin login attempts. Tenant-scoped writes are recorded separately in each
                    tenant's audit log.
                </p>
            </header>

            <Card className="grid grid-cols-1 gap-3 p-4 md:grid-cols-4" data-testid="audit-filters">
                <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Action type</Label>
                    <Select
                        value={filters.action_type}
                        onValueChange={(v) => { setPage(1); setFilters((f) => ({ ...f, action_type: v })); }}
                    >
                        <SelectTrigger data-testid="audit-filter-action"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {ACTION_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Actor user id</Label>
                    <Input
                        value={filters.actor_user_id}
                        onChange={(e) => setFilters((f) => ({ ...f, actor_user_id: e.target.value }))}
                        placeholder="…paste user id"
                        data-testid="audit-filter-actor"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">From</Label>
                    <Input
                        type="datetime-local"
                        value={filters.date_from}
                        onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
                        data-testid="audit-filter-from"
                    />
                </div>
                <div className="space-y-1.5">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">To</Label>
                    <Input
                        type="datetime-local"
                        value={filters.date_to}
                        onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
                        data-testid="audit-filter-to"
                    />
                </div>
            </Card>

            <Card className="overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Timestamp</TableHead>
                            <TableHead>Action</TableHead>
                            <TableHead>Actor</TableHead>
                            <TableHead>Target tenant</TableHead>
                            <TableHead>Target user</TableHead>
                            <TableHead>IP</TableHead>
                            <TableHead>User agent</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-zinc-500">Loading…</TableCell></TableRow>
                        ) : items.length === 0 ? (
                            <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-zinc-500">No audit entries match this filter.</TableCell></TableRow>
                        ) : items.map((it) => (
                            <TableRow key={it.id} data-testid={`audit-row-${it.id}`}>
                                <TableCell className="font-mono text-xs">
                                    {it.created_at ? formatDateTime(it.created_at, { options: { dateStyle: "medium", timeStyle: "medium" } }) : "—"}
                                </TableCell>
                                <TableCell><ActionBadge action={it.action_type} /></TableCell>
                                <TableCell className="font-mono text-xs text-zinc-500" title={it.actor_user_id || ""}>
                                    {it.actor_user_id ? it.actor_user_id.slice(-8) : "—"}
                                </TableCell>
                                <TableCell className="font-mono text-xs text-zinc-500" title={it.target_tenant_id || ""}>
                                    {it.target_tenant_id ? it.target_tenant_id.slice(-8) : "—"}
                                </TableCell>
                                <TableCell className="font-mono text-xs text-zinc-500" title={it.target_user_id || ""}>
                                    {it.target_user_id ? it.target_user_id.slice(-8) : "—"}
                                </TableCell>
                                <TableCell className="font-mono text-xs">{it.ip || "—"}</TableCell>
                                <TableCell className="max-w-[180px]">
                                    {it.user_agent ? (
                                        <TooltipProvider delayDuration={150}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <span className="line-clamp-1 cursor-help font-mono text-[11px] text-zinc-500">
                                                        {it.user_agent}
                                                    </span>
                                                </TooltipTrigger>
                                                <TooltipContent className="max-w-md break-words text-xs">
                                                    {it.user_agent}
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    ) : "—"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Card>

            <div className="flex items-center justify-between text-sm text-zinc-500">
                <span>{loading ? "…" : `${total} entries`} · Page {page} of {pageCount}</span>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" disabled={page <= 1}
                             onClick={() => setPage((p) => Math.max(1, p - 1))}
                             data-testid="audit-page-prev">Previous</Button>
                    <Button variant="outline" size="sm" disabled={page >= pageCount}
                             onClick={() => setPage((p) => p + 1)}
                             data-testid="audit-page-next">Next</Button>
                </div>
            </div>
        </div>
    );
}
